import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { fieldClasses, fieldFrameClasses, fieldHeight, fieldInputClasses } from './field';

export function Input({ className, type = 'text', ...props }: ComponentProps<'input'>) {
  return <input type={type} className={cn(fieldClasses, fieldHeight, className)} {...props} />;
}

export interface AffixInputProps extends Omit<ComponentProps<'input'>, 'prefix'> {
  leading?: ReactNode;
  suffix?: ReactNode;
  wrapperClassName?: string;
}

/**
 * Input with a leading adornment, a trailing adornment or a unit. Adornments are drawn inside the field frame. A
 * text or number `suffix` is a unit: plain muted text after the frame, with no border, fill or role, so it never
 * reads as a button. Any other `suffix` node is a trailing adornment inside the frame. `wrapperClassName` applies to
 * the outermost element: the frame, or the row of frame and unit.
 */
export function AffixInput({
  leading,
  suffix,
  className,
  wrapperClassName,
  ...props
}: AffixInputProps) {
  const unit = suffix !== '' && (typeof suffix === 'string' || typeof suffix === 'number');
  const frame = (
    <div className={cn(fieldFrameClasses, fieldHeight, unit ? 'min-w-0 flex-1' : wrapperClassName)}>
      {leading ? <span className="flex items-center pl-2.5 text-muted">{leading}</span> : null}
      <input className={cn(fieldInputClasses, className)} {...props} />
      {suffix && !unit ? (
        <span className="flex shrink-0 items-center pr-2.5 text-sm text-muted">{suffix}</span>
      ) : null}
    </div>
  );
  if (!unit) return frame;
  return (
    <div className={cn('flex min-w-0 items-center gap-1.5', wrapperClassName)}>
      {frame}
      <span className="shrink-0 text-sm text-muted">{suffix}</span>
    </div>
  );
}
